import { useState, useEffect, useCallback } from 'react'
import { message } from 'antd'
import dayjs from 'dayjs'

import { useCaseViewContext } from '../context/CaseViewContext'
import { dateTimeFormatter } from '../utils/dateTimeFormatter'

export const useProviderNotes = ({ apiClasses, apiType = 'physician' }) => {
  const { caseDetails } = useCaseViewContext()
  const { PhysicianAPI } = apiClasses || {}
  
  const isReadOnly = apiType === 'admin'

  const [loading, setLoading] = useState(false)
  const [providerNotes, setProviderNotes] = useState([])
  const [unreadNotesCount, setUnreadNotesCount] = useState(0)

  const getAllProviderNotes = useCallback(async () => {
    if (!PhysicianAPI || !caseDetails) return

    try {
      setLoading(true)
      const { data } = await PhysicianAPI.getNotes(caseDetails.caseId || caseDetails._id)

      if (!data.success) {
        throw new Error(data?.message || "Unable to fetch Physician notes. Please try again!")
      }

      const { payload } = data

      const formattedNotes = payload
        .map(note => ({
          ...note,
          createdDate: dateTimeFormatter(note.createdDate),
          updatedDate: dateTimeFormatter(note.updatedDate),
        }))
        .sort((a, b) => {
          const dateA = new Date(a.updatedDate) || new Date(a.createdDate)
          const dateB = new Date(b.updatedDate) || new Date(b.createdDate)
          return dateB - dateA
        })
      
      setProviderNotes(formattedNotes)
    } catch (error) {
      message.error(error.response?.data?.message || error.message)
    } finally {
      setLoading(false)
    }
  }, [PhysicianAPI, caseDetails])

  const addProviderNote = useCallback(async ({ title, content }) => {
    if (isReadOnly) {
      message.warning('Provider notes are read-only for admin users')
      return false
    }
    
    if (!PhysicianAPI || !caseDetails) return

    try {
      setLoading(true)
      const { data } = await PhysicianAPI.addPhysicianNote(caseDetails.caseId || caseDetails._id, {
        title,
        content,
      })

      if (!data.success) {
        throw new Error("Unable to add note. Please try again!")
      }

      message.success("Successfully added physician note")

      const newNote = { 
        ...data.payload, 
        updatedAt: new Date().toISOString(),
        createdDate: dateTimeFormatter(new Date()),
        updatedDate: dateTimeFormatter(new Date())
      }
      setProviderNotes(prevNotes => [newNote, ...prevNotes])
      
      return true
    } catch (error) {
      message.error(error.response?.data?.message || error.message)
      return false
    } finally {
      setLoading(false)
    }
  }, [PhysicianAPI, caseDetails, isReadOnly])

  const editProviderNote = useCallback(async (noteData) => {
    if (isReadOnly) {
      message.warning('Provider notes are read-only for admin users')
      return false
    }
    
    if (!PhysicianAPI) return

    try {
      setLoading(true)
      const { data } = await PhysicianAPI.updatePhysicianNote(noteData._id, noteData)

      if (!data.success) {
        throw new Error("Unable to update note. Please try again!")
      }

      setProviderNotes(prevNotes => {
        const updatedNotes = [...prevNotes]
        const editedNote = updatedNotes.find(entry => entry._id === noteData._id)
        
        if (editedNote) {
          editedNote.title = noteData.title
          editedNote.content = noteData.content
          editedNote.updatedDate = dayjs().format("MM/DD/YYYY hh:mm A")
        }
        
        return updatedNotes
      })
      
      message.success("Successfully updated physician note")
      return true
    } catch (error) {
      message.error(error.response?.data?.message || error.message)
      return false
    } finally {
      setLoading(false)
    }
  }, [PhysicianAPI, isReadOnly])

  const deleteProviderNote = useCallback(async (note) => {
    if (isReadOnly) {
      message.warning('Provider notes are read-only for admin users')
      return false
    }
    
    if (!PhysicianAPI) return

    try {
      setLoading(true)
      const { data } = await PhysicianAPI.deleteNote(note._id)

      if (!data.success) {
        throw new Error(`Unable to delete ${note.title} note. Please try again!`)
      }

      setProviderNotes(prevNotes => prevNotes.filter(n => n._id !== note._id))
      message.success(`Successfully deleted ${note.title} note`)
      return true
    } catch (error) {
      message.error(error.response?.data?.message || error.message)
      return false
    } finally {
      setLoading(false)
    }
  }, [PhysicianAPI, isReadOnly])

  const clearUnreadNotesCount = useCallback(async () => {
    if (!PhysicianAPI || !caseDetails) return

    try {
      await PhysicianAPI.clearUnreadNotesNotification(caseDetails.caseId || caseDetails._id)
    } catch (error) {
      // Error clearing unread notes notification - continue anyway
    } finally {
      setUnreadNotesCount(0)
    }
  }, [PhysicianAPI, caseDetails])

  useEffect(() => {
    if (caseDetails && PhysicianAPI) {
      getAllProviderNotes()
    }
  }, [caseDetails, getAllProviderNotes, PhysicianAPI])

  return {
    loading,
    providerNotes,
    unreadNotesCount,
    addProviderNote,
    editProviderNote,
    deleteProviderNote,
    getAllProviderNotes,
    clearUnreadNotesCount,
  }
}
